import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { peopleApi } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.jsx";

/* The core group: everyone with access, which is also the address book a
   note can be composed to. Any member may read it — you cannot work the
   board without knowing who the other reviewers are — but only an
   administrator can change it, under Access. */
export function CoreGroupPage() {
  const { isAdmin } = useAuth();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let live = true;
    peopleApi
      .list()
      .then(({ users }) => live && setState({ loading: false, users }))
      .catch((e) => live && setState({ loading: false, error: e.message }));
    return () => {
      live = false;
    };
  }, []);

  const users = (state.users || []).filter((u) => u.coreGroup !== false);
  const daily = users.filter((u) => u.daily && u.active);

  const groups = useMemo(() => {
    const map = new Map();
    for (const u of users) {
      const role = u.title?.trim() || "Other";
      if (!map.has(role)) map.set(role, []);
      map.get(role).push(u);
    }
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === "Other") return 1;
      if (b === "Other") return -1;
      return a.localeCompare(b, undefined, { sensitivity: "base" });
    });
  }, [users]);

  return (
    <div className="page">
      <div className="pagehead">
        <div>
          <h2 className="pagetitle">Core group</h2>
          <p className="pagesub">
            {users.length} on the access list · {daily.length} working the board
            each day
          </p>
        </div>
        <div className="pageacts">
          {isAdmin && (
            <Link className="btn btn-sm btn-solid" to="/access">
              Manage access
            </Link>
          )}
          <Link className="btn btn-sm" to="/">
            Back to the board
          </Link>
        </div>
      </div>

      {state.error && (
        <div className="notice">
          <b>Could not read the core group.</b> {state.error}
        </div>
      )}

      {state.loading ? (
        <p className="empty">Reading the core group…</p>
      ) : !users.length ? (
        <p className="empty">Nobody is on the access list yet.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {groups.map(([role, list]) => (
            <div className="sec" key={role}>
              <div className="sechead">
                <span className="label">
                  {role} · {list.length}
                </span>
                <span className="rule" />
              </div>
              <div className="rows">
                {list.map((u) => (
                  <div className={"prow read" + (u.active ? "" : " off")} key={u.id}>
                    <span className="pname">{u.name || <i>no name recorded</i>}</span>
                    <span className="ptitle">{u.title || "—"}</span>
                    <a className="pmail mono" href={"mailto:" + u.email}>
                      {u.email}
                    </a>
                    <div className="ptags">
                      {u.daily && <span className="tag">daily</span>}
                      {u.role === "admin" && <span className="tag admin">admin</span>}
                      {!u.active && <span className="tag off">no access</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="hintline">
        A note can only be addressed to someone on this list — that is the
        point of it being the address book.
      </p>
    </div>
  );
}
